import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import { AriaRole, ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface CardProps {
    title?: ReactNode;
    description?: ReactNode;
    titleAction?: ReactNode;
    footer?: ReactNode;
    children?: ReactNode;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
    role?: AriaRole;
    tabIndex?: number;
}

const Card = (props: CardProps) => {
    const {
        title,
        description,
        titleAction,
        footer,
        children,
        className,
        onClick,
        onKeyDown,
        role,
        tabIndex,
    } = props;

    const { t } = useTranslation("common");

    const descriptionTooltipTargetClass = `card-description-tooltip${useId()}`;

    return (
        <div
            data-card
            role={role}
            tabIndex={tabIndex}
            onClick={onClick}
            onKeyDown={onKeyDown}
            className={twMerge(
                "flex flex-col gap-4 rounded-md border border-surface-550 bg-surface-500 px-5 py-4 shadow-xs in-data-card:shadow-[0_1px_3px_rgba(0,0,0,0.05)]!",
                onClick &&
                    "cursor-pointer transition-[border-color,box-shadow,background-color] duration-150 hover:border-primary-250 hover:bg-surface-515 hover:shadow-[0_3px_9px_rgba(0,0,0,0.08)]!",
                className
            )}
        >
            {(title || description || titleAction) && (
                <div
                    data-card-header
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
                >
                    <div className="flex grow basis-1/2 flex-col gap-0.5 min-w-0 md:basis-0">
                        {(title || description) && (
                            <div className="flex items-center gap-1.5">
                                {title && (
                                    <div
                                        className={classNames(
                                            "card-title min-w-0 md:flex-1",
                                            { "flex-1": !description }
                                        )}
                                    >
                                        {title}
                                    </div>
                                )}
                                {description && (
                                    <>
                                        <Tooltip
                                            target={`.${descriptionTooltipTargetClass}`}
                                            position="top"
                                            event="both"
                                            className="max-w-sm"
                                            pt={{
                                                text: {
                                                    className:
                                                        "wrap-break-word whitespace-pre-line",
                                                },
                                            }}
                                        >
                                            {description}
                                        </Tooltip>
                                        <button
                                            type="button"
                                            aria-label={t("common:info")}
                                            className={classNames(
                                                "pi pi-info-circle shrink-0 cursor-pointer text-base leading-none text-text-300 hover:text-secondary-500 md:hidden!",
                                                descriptionTooltipTargetClass
                                            )}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                        {description && (
                            <div className="hidden text-sm text-text-300 md:block">
                                {description}
                            </div>
                        )}
                    </div>
                    {titleAction && (
                        <div className="flex-none">{titleAction}</div>
                    )}
                </div>
            )}
            {children}
            {footer}
        </div>
    );
};

export default Card;
